// src/client/screens/borrower/LoanDetailScreen.tsx
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useLoansHistory } from '@client/hooks/useLoan';
import type { LoanFixture } from '@client/lib/fixtures';
import NavBar from '@client/components/NavBar';
import Pill, { type PillTone } from '@client/components/Pill';
import SectionLabel from '@client/components/SectionLabel';
import Button from '@client/components/Button';
import { ngweeToKwachaNumber } from '@client/utils/currency';
import { formatDueIn, formatLongDate } from '@client/utils/dates';
import { INTEREST_RATES } from '@client/utils/loanUtils';

const STATUS_LABEL: Record<LoanFixture['status'], string> = {
  pending_approval: 'Pending',
  approved: 'Approved',
  disbursement_pending: 'Disbursing',
  active: 'Active',
  repaid: 'Repaid',
  delinquent: 'Overdue',
  default: 'Defaulted',
  restructured: 'Restructured',
  cancelled: 'Cancelled',
  disbursement_failed: 'Failed',
};

const STATUS_TONE: Record<LoanFixture['status'], PillTone> = {
  pending_approval: 'warning',
  approved: 'accent',
  disbursement_pending: 'accent',
  active: 'warning',
  repaid: 'success',
  delinquent: 'danger',
  default: 'danger',
  restructured: 'warning',
  cancelled: 'neutral',
  disbursement_failed: 'danger',
};

const REPAYABLE: LoanFixture['status'][] = ['active', 'delinquent', 'restructured'];

const ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--c-text)',
  cursor: 'pointer',
};

export default function LoanDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { data: history, isLoading } = useLoansHistory();
  const navigate = useNavigate();

  const loan = history?.find((l) => l.id === id) ?? null;

  if (isLoading) {
    return (
      <div className="app-shell app-shell--with-nav">
        <main className="scroll-area" style={{ padding: '20px 24px' }}>
          <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>Loading…</p>
        </main>
        <NavBar />
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="app-shell app-shell--with-nav">
        <main className="scroll-area" style={{ padding: '20px 24px' }}>
          <button type="button" aria-label="Go back" style={ICON_BTN} onClick={() => navigate(ROUTES.loansList)}>
            <ChevronLeft size={18} />
          </button>
          <p style={{ color: 'var(--c-text-muted)', fontSize: 14, marginTop: 24 }}>Loan not found.</p>
        </main>
        <NavBar />
      </div>
    );
  }

  const outstandingKwacha = ngweeToKwachaNumber(loan.outstanding_ngwee);
  const totalKwacha = ngweeToKwachaNumber(loan.total_due_ngwee);
  const paidKwacha = Math.max(0, totalKwacha - outstandingKwacha);
  const paidPct = totalKwacha > 0 ? Math.round((paidKwacha / totalKwacha) * 100) : 0;
  const tone = STATUS_TONE[loan.status];
  const canRepay = REPAYABLE.includes(loan.status);

  const disbursedAt = loan.disbursed_at ? new Date(loan.disbursed_at) : null;
  const daysElapsed = disbursedAt
    ? Math.max(0, Math.floor((Date.now() - disbursedAt.getTime()) / 86_400_000))
    : 0;
  const daysRemaining = Math.max(0, loan.term_days - daysElapsed);

  const timelineRows: Array<{ label: string; value: string }> = [
    loan.applied_at ? { label: 'Applied', value: formatLongDate(loan.applied_at) } : null,
    loan.disbursed_at ? { label: 'Disbursed', value: formatLongDate(loan.disbursed_at) } : null,
    canRepay ? { label: 'Term', value: `${daysRemaining} days left of ${loan.term_days}` } : null,
    loan.due_at ? {
      label: 'Due date',
      value: `${new Date(loan.due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · ${formatDueIn(loan.due_at)}`,
    } : null,
    loan.repaid_at ? { label: 'Repaid', value: formatLongDate(loan.repaid_at) } : null,
  ].filter((r): r is { label: string; value: string } => r !== null);

  return (
    <div className="app-shell app-shell--with-nav">
      <main className="scroll-area" style={{ padding: 0 }}>
        {/* Header */}
        <div style={{ padding: '14px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="button" aria-label="Go back" style={ICON_BTN} onClick={() => navigate(ROUTES.loansList)}>
            <ChevronLeft size={18} />
          </button>
          <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 13, color: 'var(--c-text-muted)' }}>
            {loan.loan_number}
          </span>
        </div>

        {/* Summary card */}
        <div style={{ padding: '20px 24px 0' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 20,
            padding: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="eyebrow">
                  {loan.status === 'repaid' ? 'Fully repaid' : 'Outstanding'}
                </div>
                <div style={{
                  fontSize: 48,
                  fontWeight: 400,
                  color: 'var(--c-text)',
                  letterSpacing: -2,
                  marginTop: 4,
                  lineHeight: 1,
                }}>
                  K{outstandingKwacha.toLocaleString()}
                  <span style={{ fontSize: 22, color: 'var(--c-text-muted)', fontWeight: 400 }}>.00</span>
                </div>
              </div>
              <Pill tone={tone}>{STATUS_LABEL[loan.status]}</Pill>
            </div>

            <div style={{ marginTop: 18, height: 6, background: 'var(--c-chart-track)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${paidPct}%`, height: '100%', background: 'var(--c-accent)', borderRadius: 3 }} />
            </div>
            <div style={{
              marginTop: 8,
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              color: 'var(--c-text-muted)',
              fontFamily: 'var(--ff-mono)',
            }}>
              <span>K{paidKwacha.toLocaleString()} paid</span>
              <span>{paidPct}% of K{totalKwacha.toLocaleString()}</span>
            </div>

            {canRepay && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--c-border-lt)' }}>
                <Button block size="lg" rightIcon={<ArrowRight size={18} />} onClick={() => navigate(ROUTES.repay)}>
                  Pay now
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Loan cost breakdown */}
        <div style={{ padding: '0 24px' }}>
          <SectionLabel>Breakdown</SectionLabel>
        </div>
        <div style={{ padding: '0 24px' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            overflow: 'hidden',
          }}>
            {[
              { label: 'Principal', value: `K${ngweeToKwachaNumber(loan.principal_ngwee).toLocaleString()}.00` },
              {
                label: `Interest · ${Math.round((INTEREST_RATES[loan.term_days] ?? 0) * 100)}% / ${loan.term_days}d`,
                value: `K${ngweeToKwachaNumber(loan.interest_ngwee).toLocaleString()}.00`,
              },
              { label: 'Processing fee', value: `K${ngweeToKwachaNumber(loan.processing_fee_ngwee).toLocaleString()}.00` },
              { label: 'Total repayable', value: `K${ngweeToKwachaNumber(loan.total_due_ngwee).toLocaleString()}.00` },
            ].map(({ label, value }, i, arr) => (
              <div key={label} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '13px 16px',
                borderBottom: i < arr.length - 1 ? '1px solid var(--c-border-lt)' : 'none',
              }}>
                <span style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{label}</span>
                <span style={{ fontSize: 13, color: 'var(--c-text)', fontWeight: 600, fontFamily: 'var(--ff-mono)' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div style={{ padding: '0 24px' }}>
          <SectionLabel>Timeline</SectionLabel>
        </div>
        <div style={{ padding: '0 24px' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            overflow: 'hidden',
          }}>
            {timelineRows.map(({ label, value }, i) => (
              <div key={label} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '13px 16px',
                borderBottom: i < timelineRows.length - 1 ? '1px solid var(--c-border-lt)' : 'none',
              }}>
                <span style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{label}</span>
                <span style={{ fontSize: 13, color: 'var(--c-text)', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ height: 24 }} />
      </main>

      <NavBar />
    </div>
  );
}
